import { FC } from "react";
import CustomPopup from "../../../../components/CustomPopup/CustomPopup";
import { Box, Button, Typography } from "@mui/material";
import { defaultEmptyFun } from "../../../../utils/helper";
import abcdLogo from "../../../../assets/icons/revamp-home-abcd-logo-icon.svg";
import { Colors } from "../../../../styles/constant";
import qrCode from "../../../../assets/icons/revamp-qrCode-icon.svg";
import KeyboardArrowLeftIcon from "@mui/icons-material/KeyboardArrowLeft";
import { isEmpty } from "lodash";
import "./DownloadQrCodePopup.scss";
import useCmsPageVariables from "../../../../hooks/useCmsPageVariables";
import { useAppTranslation } from "../../../../hooks/useAppTranslation";

interface Props {
  listOfItems?: string; //pass the strings by comma separated
  titleToShow?: string;
  open: boolean;
  handleOpen?: Function;
}

const DownloadQrCodePopup: FC<Props> = ({
  open,
  handleOpen = defaultEmptyFun,
  listOfItems = "",
  titleToShow = "",
}) => {
  const { t } = useAppTranslation("common");
  const downloadQrCodePopupData = useCmsPageVariables("download-qrCode-popup");

  const buildPyramidRows = (items: string[]): string[][] => {
    if (!items.length) return [];

    let totalItems = items.length;
    let rowSizes: number[] = [];

    // Step 1: Find max starting row size for a decreasing sequence (n + n-1 + n-2 + ...)
    let start = 1;
    while ((start * (start + 1)) / 2 <= totalItems) {
      start++;
    }
    start--; // Adjust to last valid

    // Step 2: Create row sizes in decreasing order
    for (let i = start; i > 0; i--) {
      rowSizes.push(i);
    }

    // Step 3: Distribute remaining items into the largest rows
    let remaining = totalItems - rowSizes.reduce((a, b) => a + b, 0);
    let idx = 0;
    while (remaining > 0) {
      rowSizes[idx]++;
      remaining--;
      idx = (idx + 1) % rowSizes.length;
    }

    // Step 4: Slice items into rows
    let rows: string[][] = [];
    let index = 0;
    for (let size of rowSizes) {
      rows.push(items.slice(index, index + size));
      index += size;
    }

    return rows;
  };

  const parseItems = (items: string) => {
    return items
      .split(",")
      .map((item) => item.trim().replace(/^"|"$/g, ""))
      .filter((item) => item.length > 0);
  };

  // Extract and clean the text field
  const rawText = downloadQrCodePopupData?.attributes?.text || "";
  const parsedArray = parseItems(rawText);
  const parsedListOfItems = parseItems(listOfItems);

  const rows = buildPyramidRows(
    isEmpty(listOfItems) ? parsedArray : parsedListOfItems,
  );

  const handleClosePopup = () => {
    handleOpen(false);
  };

  return (
    <>
      <CustomPopup
        className="downloadQrCodePopup-content-wrapper"
        open={open}
        handleOpen={handleClosePopup}
        btnLabel={t("home.actionButtons.goBack")}
        btnStartIcon={<KeyboardArrowLeftIcon />}
      >
        <Box className="downloadQrCode-main-box">
          <Box display="flex" gap="1.5rem">
            <Box
              display="flex"
              flexDirection="column"
              gap=".5rem"
              alignItems="center"
            >
              <img
                src={
                  isEmpty(
                    downloadQrCodePopupData?.attributes?.mobileImage?.data
                      ?.attributes?.url,
                  )
                    ? abcdLogo
                    : downloadQrCodePopupData?.attributes?.mobileImage?.data
                        ?.attributes?.url
                }
                style={{ width: "2.5625rem", height: "2.5625rem" }}
              />
              <Typography
                variant="h3"
                fontWeight="500"
                color={Colors.darkCharcoal}
              >
                {t("home.downloadQrCodePopup.downloadTheAppNow")}
              </Typography>
            </Box>
          </Box>
          <img
            src={
              isEmpty(
                downloadQrCodePopupData?.attributes?.forwardArrowIcon?.data
                  ?.attributes?.url,
              )
                ? qrCode
                : downloadQrCodePopupData?.attributes?.forwardArrowIcon?.data
                    ?.attributes?.url
            }
          />
          <Box
            display="flex"
            gap=".75rem"
            flexDirection="column"
            alignItems="center"
          >
            <Box
              display="flex"
              flexDirection="row"
              gap=".5rem"
              alignItems="center"
            >
              <Box
                sx={{
                  border:
                    " 1px solid var(--Neutral-Additional-BGGrey, #F1F3F6)",
                  width: "4.8438rem",
                  height: "0rem",
                }}
              ></Box>
              <Typography
                variant="h6"
                color="#7C2279"
                textTransform="uppercase"
                noWrap
              >
                {isEmpty(titleToShow)
                  ? downloadQrCodePopupData?.attributes?.title
                  : titleToShow}
              </Typography>
              <Box
                sx={{
                  border:
                    " 1px solid var(--Neutral-Additional-BGGrey, #F1F3F6)",
                  width: "4.8438rem",
                  height: "0rem",
                }}
              ></Box>
            </Box>
            <Box
              display="flex"
              flexDirection="column"
              gap=".25rem"
              alignItems="center"
            >
              {rows.map((row, rowIndex) => (
                <Box
                  key={`row-${rowIndex}`}
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  flexWrap="wrap"
                >
                  {row.map((item, itemIndex) => (
                    <Box
                      key={`item-${itemIndex}`}
                      display="flex"
                      alignItems="center"
                    >
                      <Typography variant="h6" color="#7C2279" fontWeight={400}>
                        {item}
                      </Typography>

                      {itemIndex < row.length - 1 && (
                        <Box
                          display="flex"
                          alignItems="center"
                          px={0.5} // padding on left & right instead of margin
                        >
                          <Typography
                            variant="h6"
                            color={Colors.bgGray}
                            fontWeight={400}
                          >
                            |
                          </Typography>
                        </Box>
                      )}
                    </Box>
                  ))}
                </Box>
              ))}
            </Box>
          </Box>
          <Button
            variant="contained"
            sx={{ width: "20.5rem" }}
            onClick={handleClosePopup}
          >
            <Typography variant="h4">
              {t("home.actionButtons.done") ||
                downloadQrCodePopupData?.attributes?.urlLable}
            </Typography>
          </Button>
        </Box>
      </CustomPopup>
    </>
  );
};

export default DownloadQrCodePopup;
